import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';
import SocietyCard from '../components/societies/SocietyCard';
import SocietyFilters from '../components/societies/SocietyFilters';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { societyService } from '../services/societyService';

const SocietiesPage = () => {
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    sortBy: 'rating',
    minRating: 0
  });
  const [cities, setCities] = useState([]);

  // Handle follow status change
  const handleFollowChange = (societyId, isFollowing, followerCount) => {
    setSocieties(prevSocieties => 
      prevSocieties.map(society => 
        society.id === societyId 
          ? { ...society, is_following: isFollowing, follower_count: followerCount }
          : society
      )
    );
  };

  // Load cities for dropdown
  useEffect(() => {
    const loadCities = async () => {
      try {
        const response = await societyService.getCities();
        setCities(response?.cities || []);
      } catch (error) {
        console.error('Error loading cities:', error);
        setCities([]); // Set empty array on error
      }
    };
    loadCities();
  }, []);

  // Load societies
  const loadSocieties = useCallback(async (pageNum = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: pageNum,
        page_size: 12,
        ...filters
      };

      const response = await societyService.getSocieties(params);
      
      // Safety check for response structure
      const results = response?.results || [];
      const next = response?.next;
      
      if (reset) {
        setSocieties(results);
      } else {
        setSocieties(prev => {
          const existingIds = new Set((prev || []).map((s) => s.id));
          const newItems = results.filter((s) => s.id != null && !existingIds.has(s.id));
          return [...(prev || []), ...newItems];
        });
      }
      
      setHasMore(next !== null);
      setPage(pageNum);
      setError(null);
    } catch (error) {
      setError('Failed to load societies. Please try again.');
      console.error('Error loading societies:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters]);

  // Load societies when filters change
  useEffect(() => {
    loadSocieties(1, true);
  }, [filters, loadSocieties]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Handle load more
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadSocieties(page + 1, false);
    }
  };

  // Handle search
  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
  };

  // Handle city selection
  const handleCityChange = (city) => {
    setFilters(prev => ({ ...prev, city: city }));
  };

  // Handle sort change
  const handleSortChange = (sortBy) => {
    setFilters(prev => ({ ...prev, sortBy: sortBy }));
  };

  if (loading) {
    return (
      <PageLayout title="PakPoints">
        <Helmet>
          <title>Societies - PakPoints</title>
          <meta name="description" content="Discover and explore residential societies in Pakistan. Find the perfect community for your family." />
          <meta name="keywords" content="societies, residential, communities, Pakistan, housing" />
        </Helmet>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="PakPoints">
      <Helmet>
        <title>Societies - PakPoints</title>
        <meta name="description" content="Discover and explore residential societies in Pakistan. Find the perfect community for your family." />
        <meta name="keywords" content="societies, residential, communities, Pakistan, housing" />
        <meta property="og:title" content="Societies - PakPoints" />
        <meta property="og:description" content="Discover and explore residential societies in Pakistan. Find the perfect community for your family." />
        <meta property="og:type" content="website" />
      </Helmet>


      {/* Top Ad - Removed to avoid header confusion */}

      <div className="w-full px-4 py-4 sm:px-6 sm:py-8 lg:px-8">
        <div className="flex gap-6">
          {/* Main Content Area */}
          <div className="min-w-0 flex-1">
            <div className="mx-auto max-w-7xl">
              <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
                {/* Filters first (mobile + desktop sidebar) */}
                <div className="w-full shrink-0 lg:w-1/4">
                  <div className="lg:sticky lg:top-20">
                    <SocietyFilters
                      cities={cities}
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onSearch={handleSearch}
                      onCityChange={handleCityChange}
                      onSortChange={handleSortChange}
                    />
                  </div>
                </div>

                <div className="min-w-0 lg:w-3/4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
                {error}
              </div>
            )}

            {/* Results count */}
            <div className="mb-4 lg:mb-6">
              <p className="text-sm text-gray-600 lg:text-base">
                {societies && societies.length > 0 ? `Showing ${societies.length} societies` : 'No societies found'}
                {filters.city && ` in ${filters.city}`}
                {filters.search && ` matching "${filters.search}"`}
              </p>
            </div>

            {/* Societies Grid — 2 columns on mobile, 3 on xl */}
            {societies && societies.length > 0 ? (
              <div className="grid grid-cols-2 gap-2 sm:gap-4 md:gap-5 lg:gap-6 xl:grid-cols-3">
                {societies.map((society, index) => (
                  <div
                    key={`society-${index}-${society.id}`}
                    className="min-w-0"
                  >
                    <SocietyCard society={society} onFollowChange={handleFollowChange} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No societies found</h3>
                <p className="text-gray-500">
                  Try adjusting your search criteria or browse all societies.
                </p>
              </div>
            )}

            {/* Load More Button */}
            {hasMore && societies && societies.length > 0 && (
              <div className="text-center mt-12">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                    </>
                  ) : (
                    'Load More Societies'
                  )}
                </button>
              </div>
            )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default SocietiesPage;
